import React, { useState, useEffect } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVisible, setIsVisible] = useState({});
  const [activeTab, setActiveTab] = useState('contact');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  const contactMethods = [
    {
      icon: "📧",
      title: "Email Support",
      primary: "support@emftitan.com",
      secondary: "info@emftitan.com",
      description: "Get detailed help via email",
      color: "blue",
      delay: "0ms"
    },
    {
      icon: "📞",
      title: "Phone Support", 
      primary: "1-800-EMF-TITAN",
      secondary: "Monday - Friday: 9AM - 6PM EST",
      description: "Speak with our experts directly",
      color: "green",
      delay: "100ms"
    },
    {
      icon: "💬",
      title: "Live Chat",
      primary: "Available 24/7",
      secondary: "Average response: 2 minutes",
      description: "Get instant help online",
      color: "purple",
      delay: "200ms"
    },
    {
      icon: "📍",
      title: "Visit Us",
      primary: "123 EMF Protection Lane",
      secondary: "Wellness City, WC 12345",
      description: "Schedule an in-person consultation",
      color: "orange",
      delay: "300ms"
    }
  ];

  const faqs = [
    {
      question: "How quickly will I receive my order?",
      answer: "Standard shipping takes 3-5 business days within the US. Express shipping options available for faster delivery.",
      category: "shipping"
    },
    {
      question: "Do you offer international shipping?",
      answer: "Yes, we ship worldwide to over 50 countries. International shipping times vary by location (7-14 business days).",
      category: "shipping"
    },
    {
      question: "What is your return policy?",
      answer: "We offer a 30-day money-back guarantee on all products. Items must be in original condition for full refund.",
      category: "returns"
    },
    {
      question: "Are your products scientifically tested?",
      answer: "Yes, all our EMF protection products undergo rigorous third-party testing and are certified by independent laboratories.",
      category: "products"
    },
    {
      question: "How do I know which EMF protection product is right for me?",
      answer: "Our experts can help assess your needs through a free consultation. Contact us for personalized recommendations.",
      category: "products"
    },
    {
      question: "Do you offer bulk or corporate discounts?",
      answer: "Yes, we offer special pricing for bulk orders and corporate accounts. Contact our sales team for custom quotes.",
      category: "pricing"
    }
  ];

  const businessHours = [
    { day: "Monday - Friday", hours: "9:00 AM - 6:00 PM EST", isOpen: true },
    { day: "Saturday", hours: "10:00 AM - 4:00 PM EST", isOpen: true },
    { day: "Sunday", hours: "Closed", isOpen: false }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-4 -left-4 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute top-1/4 -right-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="relative pt-16 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Hero Header */}
          <div 
            id="header"
            data-animate
            className={`text-center mb-20 transition-all duration-1000 ${isVisible.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
              Available 24/7 for support
            </div>
            <h1 className="text-6xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 mb-6">
              Contact Us
            </h1>
            <p className="text-xl md:text-2xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Get in touch with our EMF protection experts - we're here to help you stay protected
            </p>
            
            {/* Response time indicator */}
            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Average response: 2 hours</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span>Expert support team</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                <span>50,000+ customers helped</span>
              </div>
            </div>
          </div>

          {/* Contact Methods Grid */}
          <div 
            id="contact-methods"
            data-animate
            className={`mb-20 transition-all duration-1000 delay-200 ${isVisible['contact-methods'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-800 mb-4">How Can We Help You?</h2>
              <p className="text-slate-600">Choose your preferred way to get in touch</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {contactMethods.map((method, index) => (
                <div 
                  key={index}
                  className="group bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 text-center cursor-pointer"
                  style={{animationDelay: method.delay}}
                >
                  <div className={`w-16 h-16 bg-gradient-to-br from-${method.color}-400 to-${method.color}-600 rounded-xl mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <div className="text-white text-2xl">{method.icon}</div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{method.title}</h3>
                  <p className="text-slate-900 font-semibold mb-1">{method.primary}</p>
                  <p className="text-slate-600 text-sm mb-3">{method.secondary}</p>
                  <p className="text-slate-500 text-xs">{method.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Contact Form - Takes 2 columns */}
            <div 
              id="contact-form"
              data-animate
              className={`lg:col-span-2 transition-all duration-1000 delay-300 ${isVisible['contact-form'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-xl border border-white/20">
                <div className="mb-8">
                  <div className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 rounded-full text-sm font-medium mb-4">
                    ✉️ Send Message
                  </div>
                  <h2 className="text-3xl font-bold text-slate-800 mb-2">Send us a Message</h2>
                  <p className="text-slate-600">We'll get back to you within 24 hours</p>
                </div>
                
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-3">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                        placeholder="Your full name"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-3">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-slate-700 mb-3">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                    >
                      <option value="">Select a subject</option>
                      <option value="product-inquiry">🛍️ Product Inquiry</option>
                      <option value="technical-support">🔧 Technical Support</option>
                      <option value="order-status">📦 Order Status</option>
                      <option value="return-exchange">↩️ Return/Exchange</option>
                      <option value="general-question">❓ General Question</option>
                      <option value="consultation">👥 Free Consultation</option>
                      <option value="other">📝 Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-3">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full px-4 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/50 backdrop-blur-sm resize-none"
                      placeholder="How can we help you? Please provide as much detail as possible..."
                    ></textarea>
                  </div>

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="group w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white py-4 px-8 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-3"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Sending Message...
                      </>
                    ) : (
                      <>
                        <span>Send Message</span>
                        <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center group-hover:translate-x-1 transition-transform duration-300">
                          <span className="text-white text-xs">→</span>
                        </div>
                      </>
                    )}
                  </button>
                </div>

                {/* Trust indicators */}
                <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-slate-200">
                  <div className="text-center">
                    <div className="text-blue-600 font-bold text-lg">24hrs</div>
                    <div className="text-slate-500 text-xs">Response Time</div>
                  </div>
                  <div className="text-center">
                    <div className="text-green-600 font-bold text-lg">99.9%</div>
                    <div className="text-slate-500 text-xs">Satisfaction Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-purple-600 font-bold text-lg">50K+</div>
                    <div className="text-slate-500 text-xs">Happy Customers</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              
              {/* Business Hours */}
              <div 
                id="business-hours"
                data-animate
                className={`transition-all duration-1000 delay-400 ${isVisible['business-hours'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-sm">🕒</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">Business Hours</h3>
                  </div>
                  <div className="space-y-3">
                    {businessHours.map((schedule, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-slate-600">{schedule.day}</span>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${schedule.isOpen ? 'bg-green-500' : 'bg-red-400'}`}></div>
                          <span className={`font-medium text-sm ${schedule.isOpen ? 'text-slate-800' : 'text-slate-500'}`}>
                            {schedule.hours}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div 
                id="quick-links"
                data-animate
                className={`transition-all duration-1000 delay-500 ${isVisible['quick-links'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-sm">⚡</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">Quick Actions</h3>
                  </div>
                  <div className="space-y-3">
                    <button className="w-full text-left p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors group">
                      <div className="flex items-center justify-between">
                        <span className="text-blue-700 font-medium">Track Your Order</span>
                        <span className="text-blue-500 group-hover:translate-x-1 transition-transform">→</span>
                      </div>
                    </button>
                    <button className="w-full text-left p-3 rounded-lg bg-green-50 hover:bg-green-100 transition-colors group">
                      <div className="flex items-center justify-between">
                        <span className="text-green-700 font-medium">Free EMF Assessment</span>
                        <span className="text-green-500 group-hover:translate-x-1 transition-transform">→</span>
                      </div>
                    </button>
                    <button className="w-full text-left p-3 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors group">
                      <div className="flex items-center justify-between">
                        <span className="text-purple-700 font-medium">Download Catalog</span>
                        <span className="text-purple-500 group-hover:translate-x-1 transition-transform">→</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div 
                id="emergency"
                data-animate
                className={`transition-all duration-1000 delay-600 ${isVisible.emergency ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-2xl p-6 shadow-lg">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-full mx-auto mb-3 flex items-center justify-center">
                      <span className="text-white text-xl">🚨</span>
                    </div>
                    <h3 className="text-lg font-bold text-red-800 mb-2">Emergency Support</h3>
                    <p className="text-red-700 text-sm mb-3">
                      For urgent technical issues or order emergencies
                    </p>
                    <div className="bg-white/50 rounded-lg p-3">
                      <div className="font-bold text-red-800">1-800-EMF-HELP</div>
                      <div className="text-red-600 text-sm">(363-4357) • Available 24/7</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div 
            id="faq"
            data-animate
            className={`mt-20 transition-all duration-1000 delay-700 ${isVisible.faq ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-xl border border-white/20">
              <div className="text-center mb-12">
                <div className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-700 rounded-full text-sm font-medium mb-4">
                  ❓ FAQ
                </div>
                <h2 className="text-4xl font-bold text-slate-800 mb-4">Frequently Asked Questions</h2>
                <p className="text-slate-600">Quick answers to common questions</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-white/50 rounded-xl p-6 hover:shadow-md transition-shadow duration-200">
                    <h3 className="font-bold text-slate-800 mb-3 leading-tight">{faq.question}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{faq.answer}</p>
                    <div className="mt-3">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${{
                        'shipping': 'bg-blue-100 text-blue-700',
                        'returns': 'bg-green-100 text-green-700', 
                        'products': 'bg-purple-100 text-purple-700',
                        'pricing': 'bg-orange-100 text-orange-700'
                      }[faq.category]}`}>
                        {faq.category}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="text-center mt-8">
                <button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  View All FAQs
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;